import type React from 'react'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from './Icon'
import { useClickOutside } from '../../hooks/useClickOutside'
import type { Parameter } from '../../features/shared/domain/models/parameters.ts'
import Spinner from './Spinner.tsx'

interface MultiSelectProps {
  filterNameKey: string
  options: Parameter | undefined
  onChange: (selected: string[]) => void
  hasSelectAll?: boolean
  initialSelected?: string[] | undefined
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  filterNameKey,
  options,
  onChange,
  hasSelectAll,
  initialSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState<string[]>(
    initialSelected ?? []
  )
  const dropdownRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()

  useClickOutside({
    itemRef: dropdownRef,
    triggerRef: buttonRef,
    isOpen,
    setIsOpen,
  })

  const handleSelectAll = () => {
    if (selectedOptions.length > 0) {
      onChange([])
      setSelectedOptions([])
    }
  }

  const handleOptionToggle = useCallback(
    (option: string) => {
      const newSelectedOptions = selectedOptions.includes(option)
        ? selectedOptions.filter((selected) => selected !== option)
        : [...selectedOptions, option]
      setSelectedOptions(newSelectedOptions)
      onChange(newSelectedOptions)
    },
    [selectedOptions, setSelectedOptions, onChange]
  )

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }, [selectedOptions])

  const selectedEntries = options
    ? Object.entries(options).filter(([option]) =>
        selectedOptions.includes(option)
      )
    : []
  const notSelectedEntries = options
    ? Object.entries(options).filter(
        ([option]) => !selectedOptions.includes(option)
      )
    : []

  return (
    <div className="relative inline-block w-full" ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`inline-flex h-9 w-full items-center justify-between gap-2 rounded border border-zinc-500 bg-transparent px-4 py-2 text-sm leading-5 text-white hover:bg-white/10`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="flex items-center gap-2">
          <span>{t(filterNameKey)}</span>
          {selectedOptions.length > 0 && (
            <span className="text-xs">({selectedOptions.length})</span>
          )}
        </div>
        <Icon name="chevronDown" width={16} />
      </button>

      {isOpen && (
        <div
          ref={scrollContainerRef}
          className="absolute z-10 mt-2 max-h-96 w-full cursor-pointer overflow-y-auto rounded-md bg-neutral-900 shadow-lg ring-1 ring-black/5"
        >
          {options ? (
            <>
              {hasSelectAll && (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2 hover:bg-zinc-700"
                  onClick={handleSelectAll}
                >
                  <input
                    type="checkbox"
                    className="h-4 min-w-4 rounded-sm border border-zinc-300"
                    checked={selectedOptions.length === 0}
                    readOnly
                  />
                  <span className="text-sm capitalize text-white">
                    {t('ls_filter_dropdown_all')}
                  </span>
                </button>
              )}

              {selectedEntries.map(([option, quantity]) => (
                <label
                  key={option}
                  className="flex items-center justify-between gap-2 px-4 py-2 hover:bg-zinc-700"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 min-w-4 rounded-sm border border-zinc-300"
                      checked={true}
                      onChange={() => handleOptionToggle(option)}
                    />
                    <span className="text-sm capitalize text-white">
                      {option}
                    </span>
                  </div>
                  <span className="text-xs text-zinc-500">{quantity}</span>
                </label>
              ))}

              {notSelectedEntries.map(([option, quantity]) => (
                <label
                  key={option}
                  className="flex items-center justify-between gap-2 px-4 py-2 hover:bg-zinc-700"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="h-4 min-w-4 rounded-sm border border-zinc-300"
                      checked={false}
                      onChange={() => handleOptionToggle(option)}
                    />
                    <span className="text-sm capitalize text-white">
                      {option}
                    </span>
                  </div>
                  <span className="text-xs text-zinc-500">{quantity}</span>
                </label>
              ))}
            </>
          ) : (
            <Spinner className="mx-auto my-4 h-6 w-6" />
          )}
          {options?.length === 0 && (
            <p className="py-4 text-center text-gray-500">
              {t('ls_filter_no_options')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default MultiSelect
